import { api } from "dicomweb-client";
import { DICOM_WEB_ENTRYPOINT } from "config/api";
import { HTTPErrorMessage } from "shared/utils/error";


const redirectToLoginIf401 = async (error: Error & { status?: number }) => {
  if ([401, 403].includes(error.status)) {
    try {
      await fetch("/auth/login", {
        method: "GET",
        credentials: "include",
      });
    } catch (e) {
      console.error("Authentication failed", e);
    }
  } else if (error.status && error.status > 400) {
    error.message = `Dicom Web : ${HTTPErrorMessage[error.status]}`
  }
};

export const dicomWebClient = new api.DICOMwebClient({
  url: DICOM_WEB_ENTRYPOINT,
  verbose: false,
  errorInterceptor: redirectToLoginIf401,
  withCredentials: true,
});

export const dicomOptions = {
  staleTime: 1000 * 60 * 60 * 24, // 1 hour
  cacheTime: 1000 * 60 * 60 * 24, // 1 hour,
  refetchOnMount: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
  retry: 1,
  retryDelay: 3000,
  useErrorBoundary: true
} as const;

export async function fetchStudyMetadata(
  studyInstanceUID: string,
  patientId?: string
) {
  if (!studyInstanceUID) {
    return [];
  }
  let url = `${DICOM_WEB_ENTRYPOINT}/studies/${studyInstanceUID}/metadata`;
  if (patientId) {
    const params = new URLSearchParams({ "OtherPatientIDsSequence.PatientID": patientId })
    url = `${url}?${params}`
  }
  return await fetch(url, {
    method: "GET",
    headers: {
      accept: "application/dicom+json",
    },
    mode: "cors",
    credentials: "include",
    referrer: window.location.href,
  }).then(res => res.json());
}

export async function fetchSeries(studyInstanceUID: string, patientId: string) {
  if (!studyInstanceUID) {
    return [];
  }
  const series = await dicomWebClient.searchForSeries({
    studyInstanceUID,
    queryParams: { "OtherPatientIDsSequence.PatientID": patientId },
    withCredentials: true
  });
  if (series.length === 0) {
    // Handle the "not found" case. This assumes an empty array means not found.
    throw new Error(`No study found for ${studyInstanceUID}`);
  } else {
    // Handle the found series
    return series;
  }
}

export async function fetchSeriesMetadata(
  studyInstanceUID: string,
  seriesInstanceUID: string
) {
  if (!studyInstanceUID || !seriesInstanceUID) {
    return [];
  }
  return await dicomWebClient
    .retrieveSeriesMetadata({ studyInstanceUID, seriesInstanceUID, withCredentials: true })
}


/**
 * Search studies
 * @param  PatientID is the dicom tag
 * @param  patientId is the Qyscore business id
 * @returns All the studies of a specific patient
 */
export async function searchPatientStudies(patient_id: string) {
  return await dicomWebClient.searchForStudies({
    queryParams: { "OtherPatientIDsSequence.PatientID": patient_id },
    withCredentials: true
  })
}

export async function retrieveInstance(studyInstanceUID: string, seriesInstanceUID: string, sopInstanceUID: string) {
  return await dicomWebClient.retrieveInstance({ studyInstanceUID, seriesInstanceUID, sopInstanceUID, withCredentials: true })
}
